import { useSnackbar } from "notistack";
import { useState } from "react";
import { useApiMutation, type WithDynamicUrl } from "../../../hooks";
import type {
  RunTestByIdParams,
  Test,
  TestContext,
  TestMatrix,
  TestVariable,
} from "../../../types";

export function useTestMutations(
  testId: string | undefined,
  refetchTest: () => void,
  refetchContext: () => void,
) {
  const { enqueueSnackbar } = useSnackbar();
  const [isEditingTestName, setIsEditingTestName] = useState(false);
  const [editedTestName, setEditedTestName] = useState("");

  const { mutate: runTest, isPending: isMutating } = useApiMutation<
    Pick<RunTestByIdParams, "allow_fix" | "run_matrix" | "matrix_ids">,
    RunTestByIdParams
  >("POST", `/tests/${testId}/run`, {
    onSuccess: () => {
      enqueueSnackbar("Test run started successfully!", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      refetchTest();
    },
  });

  const {
    mutate: createContext,
    mutateAsync: createContextAsync,
    isPending: isCreating,
  } = useApiMutation<Partial<TestContext>, TestContext>("POST", `/tests/${testId}/context`, {
    onSuccess: () => {
      refetchContext();
      enqueueSnackbar("Test context created successfully", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to create test context: ${error.message}`, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: moveContext, isPending: isMoving } = useApiMutation<
    WithDynamicUrl<{ new_order: number }>,
    TestContext
  >("PUT", "", {
    onSuccess: () => {
      refetchContext();
      enqueueSnackbar("Test context moved successfully", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to move test context: ${error.message}`, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const {
    mutate: updateContext,
    mutateAsync: updateContextAsync,
    isPending: isUpdatingContext,
  } = useApiMutation<WithDynamicUrl<Partial<TestContext>>, TestContext>("PUT", "", {
    onSuccess: () => {
      refetchContext();
      enqueueSnackbar("Test context updated successfully", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update test context: ${error.message}`, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const {
    mutate: deleteContext,
    mutateAsync: deleteContextAsync,
    isPending: isDeletingContext,
  } = useApiMutation<WithDynamicUrl<void>, void>("DELETE", "", {
    onSuccess: () => {
      refetchContext();
      enqueueSnackbar("Test context deleted successfully", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to delete test context: ${error.message}`, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: updateTest, isPending: isUpdatingTest } = useApiMutation<
    WithDynamicUrl<{
      name?: string;
      matrix?: TestMatrix;
      is_positive?: boolean;
      priority?: string;
      severity?: string;
      reasoning?: string;
      success_criteria?: string;
    }>,
    Test
  >("PUT", "", {
    onSuccess: () => {
      setIsEditingTestName(false);
      enqueueSnackbar("Test updated successfully", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      refetchTest();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update test: ${error.message}`, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: updateVariables, isPending: isUpdatingVariables } = useApiMutation<
    { variables: TestVariable[] },
    Test
  >("PUT", `/tests/${testId}/variables`, {
    onSuccess: () => {
      enqueueSnackbar("Variables updated successfully", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      refetchTest();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update variables: ${error.message}`, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: duplicateTest, isPending: isDuplicating } = useApiMutation<void, Test>(
    "POST",
    `/tests/${testId}/duplicate`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test duplicated successfully!", {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to duplicate test: ${error.message}`, {
          variant: "error",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
    },
  );

  const { mutate: regenerateDescription, isPending: isRegeneratingDescription } = useApiMutation<
    void,
    Test
  >("POST", `/tests/${testId}/regenerate-description`, {
    onSuccess: () => {
      enqueueSnackbar("Test description regenerated successfully!", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      refetchTest();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to regenerate description: ${error.message}`, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const handleVariablesChange = (variables: TestVariable[]) => {
    updateVariables({ variables });
  };

  const startEditingTestName = (test: Test) => {
    setEditedTestName(test.name);
    setIsEditingTestName(true);
  };

  const cancelEditingTestName = () => {
    setIsEditingTestName(false);
  };

  const saveTestName = (test: Test) => {
    updateTest({
      name: editedTestName,
      _url: `/tests/${test.id}`,
    });
  };

  return {
    runTest,
    isMutating,
    createContext,
    createContextAsync,
    isCreating,
    moveContext,
    isMoving,
    updateContext,
    updateContextAsync,
    isUpdatingContext,
    deleteContext,
    deleteContextAsync,
    isDeletingContext,
    updateTest,
    isUpdatingTest,
    updateVariables,
    isUpdatingVariables,
    duplicateTest,
    isDuplicating,
    regenerateDescription,
    isRegeneratingDescription,
    handleVariablesChange,
    isEditingTestName,
    editedTestName,
    setEditedTestName,
    startEditingTestName,
    cancelEditingTestName,
    saveTestName,
  };
}
